/**
 * AWS Platform - Lambda, S3, DynamoDB, Bedrock yonetimi
 */

import axios from 'axios'
import { AlertCircle, Cloud, Database, HardDrive, Loader2, Plus, RefreshCw, Server, Trash2, Zap } from 'lucide-react'
import { useEffect, useState } from 'react'
import { API_URL } from '../../../config/api'

interface AWSAccount {
  id: number
  account_id: string
  region: string
  label: string
  lambda_count: number
  s3_count: number
  dynamodb_count: number
  bedrock_models: number
  status: string
  use_for_llm?: number
  connected_at: string
}

interface ConnectForm {
  access_key_id: string
  secret_access_key: string
  region: string
  label: string
}

const AWS_REGIONS = [
  'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2',
  'eu-west-1', 'eu-west-2', 'eu-central-1',
  'ap-southeast-1', 'ap-southeast-2', 'ap-northeast-1'
]

export default function AWSPage() {
  const [accounts, setAccounts] = useState<AWSAccount[]>([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<ConnectForm>({ access_key_id: '', secret_access_key: '', region: 'us-east-1', label: '' })

  const fetchAccounts = async () => {
    try {
      const token = localStorage.getItem('token')
      const resp = await axios.get<{ accounts: AWSAccount[] }>(`${API_URL}/api/aicloud/aws/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setAccounts(resp.data.accounts ?? [])
    } catch {
      setAccounts([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void fetchAccounts() }, [])

  const handleConnect = async () => {
    if (!form.access_key_id || !form.secret_access_key) {
      setError('Access Key ID and Secret Access Key are required')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const token = localStorage.getItem('token')
      await axios.post(`${API_URL}/api/aicloud/aws/connect`, form, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setShowAdd(false)
      setForm({ access_key_id: '', secret_access_key: '', region: 'us-east-1', label: '' })
      void fetchAccounts()
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } } }
      setError(err.response?.data?.detail ?? 'Failed to connect')
    } finally {
      setSaving(false)
    }
  }

  const handleSync = async (id: number) => {
    try {
      const token = localStorage.getItem('token')
      await axios.post(`${API_URL}/api/aicloud/aws/accounts/${id}/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      })
      void fetchAccounts()
    } catch {
      setError('Sync failed')
    }
  }

  const handleDisconnect = async (id: number) => {
    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/api/aicloud/aws/accounts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setAccounts(prev => prev.filter(a => a.id !== id))
    } catch {
      setError('Failed to disconnect')
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: 800 }}>Amazon Web Services</h2>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Manage your Lambda functions, S3 buckets, and AI models.</p>
        </div>
        {!showAdd && (
          <button onClick={() => setShowAdd(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px', background: '#ff9900', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}>
            <Plus size={16} /> Add AWS Account
          </button>
        )}
      </div>

      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '10px', color: '#ef4444', fontSize: '13px', marginBottom: '20px' }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {showAdd && (
        <div style={{ padding: '24px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '16px', marginBottom: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>Access Key ID</label>
              <input type="text" placeholder="AKIA..." value={form.access_key_id}
                onChange={e => setForm(p => ({ ...p, access_key_id: e.target.value }))}
                style={{ width: '100%', padding: '11px 14px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', color: 'var(--text-primary)', fontSize: '14px', outline: 'none' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>Secret Access Key</label>
              <input type="password" placeholder="••••••••••••••••" value={form.secret_access_key}
                onChange={e => setForm(p => ({ ...p, secret_access_key: e.target.value }))}
                style={{ width: '100%', padding: '11px 14px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', color: 'var(--text-primary)', fontSize: '14px', outline: 'none' }} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>Default Region</label>
              <select value={form.region} onChange={e => setForm(p => ({ ...p, region: e.target.value }))}
                style={{ width: '100%', padding: '11px 14px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', color: 'var(--text-primary)', fontSize: '14px', outline: 'none', appearance: 'none' }}>
                {AWS_REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>Account Label</label>
              <input type="text" placeholder="e.g. My Production" value={form.label}
                onChange={e => setForm(p => ({ ...p, label: e.target.value }))}
                style={{ width: '100%', padding: '11px 14px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', color: 'var(--text-primary)', fontSize: '14px', outline: 'none' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={() => void handleConnect()} disabled={saving}
              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', background: '#ff9900', color: 'white', border: 'none', borderRadius: '10px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 700 }}>
              {saving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Zap size={16} />}
              {saving ? 'Connecting AWS...' : 'Connect Now'}
            </button>
            <button onClick={() => setShowAdd(false)}
              style={{ padding: '12px 24px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: '10px', cursor: 'pointer', fontSize: '14px' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#ff9900', margin: '0 auto' }} />
        </div>
      ) : accounts.length === 0 ? (
        <div style={{ padding: '60px', textAlign: 'center', background: 'var(--card-bg)', border: '2px dashed var(--border-color)', borderRadius: '20px' }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(255,153,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <Cloud size={32} color="#ff9900" />
          </div>
          <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '8px' }}>No AWS Accounts</h3>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', maxWidth: '300px', margin: '0 auto 20px' }}>Connect your AWS account to start deploying agents at the edge.</p>
          <button onClick={() => setShowAdd(true)} style={{ background: '#ff9900', color: 'white', border: 'none', padding: '10px 24px', borderRadius: '10px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
            Setup Connection
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {accounts.map(acc => (
            <div key={acc.id} style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '16px', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid var(--border-color)', background: 'linear-gradient(135deg, rgba(255,153,0,0.06) 0%, transparent 100%)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <div style={{ width: '42px', height: '42px', borderRadius: '12px', background: 'rgba(255,153,0,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Server size={22} color="#ff9900" />
                  </div>
                  <div>
                    <div style={{ fontSize: '16px', fontWeight: 700 }}>{acc.label || acc.account_id}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#10b981' }}>● connected</span>
                      <span>•</span>
                      <span>{acc.region}</span>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={() => void handleSync(acc.id)} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: 'transparent', border: '1px solid var(--border-color)', borderRadius: '8px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '13px' }}>
                    <RefreshCw size={13} /> Sync
                  </button>
                  <button onClick={() => void handleDisconnect(acc.id)} style={{ padding: '8px', color: '#ef4444', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', padding: '24px' }}>
                {[
                  { label: 'Lambda', count: acc.lambda_count, icon: <Zap size={18} />, color: '#ff9900' },
                  { label: 'S3 Buckets', count: acc.s3_count, icon: <HardDrive size={18} />, color: '#10b981' },
                  { label: 'DynamoDB', count: acc.dynamodb_count, icon: <Database size={18} />, color: '#6366f1' },
                  { label: 'Bedrock', count: acc.bedrock_models, icon: <Cloud size={18} />, color: '#f59e0b' },
                ].map(r => (
                  <div key={r.label} style={{ padding: '16px', background: 'var(--body-bg)', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
                    <div style={{ marginBottom: '8px', color: r.color }}>{r.icon}</div>
                    <div style={{ fontSize: '24px', fontWeight: 800 }}>{r.count}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 600 }}>{r.label}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`}</style>
    </div>
  )
}
